import { useMemo } from "react";

import { useAddTournamentNamingKey } from "@/hooks";
import { MessageKey } from "@/i18n";
import type { UpdateLeaguePageTabId } from "@/pages/league/UpdateLeaguePage";
import { KnownCapability } from "@/types/capability.types";
import {
  IconAthlete,
  IconBanknote,
  IconBlocks,
  IconBuilding,
  IconCalendar,
  IconCheckCircle,
  IconHandshake,
  IconHistory,
  IconHome,
  IconImages,
  IconInfoFilled,
  IconLeaderboard,
  IconLicense,
  IconMail,
  IconMedal,
  IconNews,
  IconPeople,
  IconSettings,
  IconSparkle,
  IconStar,
  IconUsers,
  InputIcon,
} from "@/ui";

export type LeagueNavGroupType = "main" | "settings";

export type LeagueNavItem = {
  /** Stable id, also used as the trailing route segment(s) under `/league/:id/`. */
  type: string;
  labelKey: MessageKey;
  icon: InputIcon;
  /** Rest path appended after `/league/:id/`, e.g. `overview` or `settings/general`. */
  path: string;
  /**
   * When set, this settings item targets a tab of the league-scoped Update page
   * (`/league/:id/edit/<editTabId>`) instead of `path`. Active state then matches
   * that edit route — and the `general` tab is also active on the bare `/edit`
   * default. Items without a matching Update tab keep `path` as a placeholder.
   */
  editTabId?: UpdateLeaguePageTabId;
  /**
   * Capability key granting the item on the league's capability set (strict RBAC),
   * checked in `LeagueGroupsList` — see docs/capabilities.md. Items without a key are visible to anyone who can
   * open the league (`overview`, `news`, `gallery` — read-open surfaces with no
   * READ capability key); `auditLog` is gated separately via
   * `useAuditLogPermissions` (ancestor-aware audit keys).
   */
  capability?: KnownCapability;
};

export type LeagueNavGroup = {
  type: LeagueNavGroupType;
  labelKey: MessageKey;
  items: LeagueNavItem[];
};

/**
 * Rest path (after `/league/:id/`) a nav item navigates to. Items with an
 * `editTabId` point at the league-scoped Update page tab; everything else uses
 * its own `path`.
 */
export const getLeagueNavItemPath = (item: LeagueNavItem): string =>
  item.editTabId ? `edit/${item.editTabId}` : item.path;

/**
 * League-scoped sidebar navigation model. MAIN items map to real, working
 * `/league/:id/<page>` pages; every SETTINGS item carries an `editTabId` and
 * targets a league-scoped Update page tab at `/league/:id/edit/<editTabId>`,
 * mirroring `UpdateLeaguePageTabIdValues` one-for-one — same items, same order,
 * Custom fees last. (`path` is only the fallback for a settings item that later
 * gets its own `/league/:id/settings/<slug>` page.)
 *
 * Items naming on the tournament term resolve through `useAddTournamentNamingKey`,
 * so a `league` container reads "Stages" / "Stage categories" — see
 * `CurrentLeagueNamingProvider`, which the sidebar's league panel mounts.
 */
export const useLeagueNavGroups = (): LeagueNavGroup[] => {
  const addTournamentNamingKey = useAddTournamentNamingKey();

  return useMemo(
    () => [
      {
        type: "main",
        labelKey: "league.nav.groups.main",
        items: [
          {
            type: "overview",
            labelKey: "league.pages.details.tabs.overview",
            icon: IconHome,
            path: "overview",
          },
          {
            type: "tournaments",
            capability: "TOURNAMENT:READ",
            labelKey: addTournamentNamingKey("league.pages.details.tabs.tournaments"),
            icon: IconCalendar,
            path: "tournaments",
          },
          {
            type: "results",
            // Mirrors the league-wide results page gate
            // (`useLeagueOverviewPermissions().canSeeTournamentResults`) and the
            // overview tile's results tab — both read `STANDINGS:READ` on the
            // league's set.
            capability: "STANDINGS:READ",
            labelKey: addTournamentNamingKey("league.pages.results.title"),
            icon: IconMedal,
            path: "results",
          },
          {
            type: "athletes",
            capability: "PLAYER:READ",
            labelKey: "league.pages.details.tabs.athletes",
            icon: IconAthlete,
            path: "athletes",
          },
          {
            type: "teams",
            capability: "TEAM:READ",
            labelKey: "league.pages.details.tabs.teams",
            icon: IconPeople,
            path: "teams",
          },
          {
            type: "rankings",
            capability: "RANKING:READ",
            labelKey: "league.pages.details.tabs.rankings",
            icon: IconLeaderboard,
            path: "rankings",
          },
          {
            type: "news",
            labelKey: "league.pages.details.tabs.news",
            icon: IconNews,
            path: "news",
          },
          {
            type: "gallery",
            labelKey: "league.pages.details.tabs.gallery",
            icon: IconImages,
            path: "gallery",
          },
          {
            type: "partners",
            // Mirrors the league partners page gate (`usePartnerPermissions`,
            // `LEAGUE_PARTNER:READ` on the league set) — without the key a
            // connection viewer whose connection does not expose LEAGUE_PARTNER
            // would see the item and land on ForbiddenPage.
            capability: "LEAGUE_PARTNER:READ",
            labelKey: "league.pages.details.tabs.partners",
            icon: IconHandshake,
            path: "partners",
          },
          {
            type: "transactions",
            capability: "LEAGUE_FINANCE:READ",
            labelKey: "league.pages.details.tabs.transactions",
            icon: IconBanknote,
            path: "transactions",
          },
          {
            type: "auditLog",
            labelKey: "league.pages.details.tabs.auditLog",
            icon: IconHistory,
            path: "audit-logs",
          },
        ],
      },
      {
        type: "settings",
        labelKey: "general.navigation.groups.settings",
        // Shares its relative order with `UpdateLeaguePageTabIdValues`
        // (src/pages/league/UpdateLeaguePage/updateLeagueTabs.ts), matched by
        // `editTabId` — keep the two in step.
        items: [
          {
            type: "general",
            capability: "LEAGUE:READ",
            labelKey: "league.nav.settings.general",
            icon: IconSettings,
            path: "settings/general",
            editTabId: "general",
          },
          {
            type: "tournamentCategories",
            capability: "CATEGORY:READ",
            labelKey: addTournamentNamingKey("league.nav.settings.tournamentCategories"),
            icon: IconStar,
            path: "edit/tournamentCategories",
            editTabId: "tournamentCategories",
          },
          {
            type: "rankingCategories",
            capability: "RANKING:READ",
            labelKey: "league.nav.settings.rankingCategories",
            icon: IconMedal,
            path: "edit/rankingCategories",
            editTabId: "rankingCategories",
          },
          {
            type: "rankingPoints",
            capability: "RANKING:READ",
            labelKey: "league.nav.settings.rankingPoints",
            icon: IconLeaderboard,
            path: "edit/rankingPoints",
            editTabId: "rankingPoints",
          },
          {
            type: "leagueSetup",
            capability: "LEAGUE:READ",
            labelKey: "league.nav.settings.leagueSetup",
            icon: IconBuilding,
            path: "settings/league-setup",
            editTabId: "leagueSetup",
          },
          {
            type: "team",
            capability: "LEAGUE_MANAGER:READ",
            // Reuses the sidebar's Management team label.
            labelKey: "general.navigation.links.label.managementTeam",
            icon: IconUsers,
            path: "edit/team",
            editTabId: "team",
          },
          {
            type: "factSheet",
            capability: "LEAGUE:READ",
            labelKey: "league.nav.settings.factSheet",
            icon: IconInfoFilled,
            path: "edit/factSheet",
            editTabId: "factSheet",
          },
          {
            type: "documentsRegulations",
            capability: "LEAGUE:READ",
            labelKey: "league.nav.settings.documentsRegulations",
            icon: IconLicense,
            path: "edit/documents",
            editTabId: "documents",
          },
          {
            type: "integrations",
            capability: "LEAGUE:READ",
            labelKey: "general.navigation.links.label.integrations",
            icon: IconBlocks,
            path: "settings/integrations",
            editTabId: "integrations",
          },
          {
            type: "extras",
            capability: "LEAGUE:READ",
            labelKey: "league.pages.create.steps.extras",
            icon: IconSparkle,
            path: "edit/extras",
            editTabId: "extras",
          },
          {
            type: "contactDetails",
            capability: "LEAGUE:READ",
            labelKey: "general.form.contactDetails.title",
            icon: IconMail,
            path: "settings/contact-details",
            editTabId: "contact",
          },
          {
            type: "status",
            capability: "LEAGUE:READ",
            labelKey: "general.form.status.label",
            icon: IconCheckCircle,
            path: "edit/status",
            editTabId: "status",
          },
          {
            type: "customFees",
            // Fees are keyed by their own capability — the matching Update page
            // tab is hidden without `LEAGUE_FEE:READ`, so the sidebar item has to
            // disappear with it instead of leading to a missing tab.
            capability: "LEAGUE_FEE:READ",
            labelKey: "league.nav.settings.customFees",
            icon: IconBanknote,
            path: "edit/fees",
            editTabId: "fees",
          },
        ],
      },
    ],
    [addTournamentNamingKey]
  );
};
